import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AuthModal from "@/components/AuthModal";
import logo from "@/assets/logo.png";
import SEO from "@/components/SEO";
import { ALLOWED_PATTERNS } from "@/lib/previewRehearsalRequest";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const practiceReturn = new URLSearchParams(location.search).get("next") === "rehearsal";
  const { user, loading } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(true);

  // If user is already logged in, redirect to return URL or dashboard
  useEffect(() => {
    if (!loading && user) {
      // Check both session and local storage for return URL (local survives email confirm)
      const returnUrl = sessionStorage.getItem("greyrock_return_url") || localStorage.getItem("greyrock_return_url");
      const pendingAnalysis = localStorage.getItem("greyrock_pending_analysis");

      // Clean up
      sessionStorage.removeItem("greyrock_return_url");
      localStorage.removeItem("greyrock_return_url");

      if (practiceReturn || returnUrl === "/login?next=rehearsal") {
        const savedPattern = sessionStorage.getItem("greyrock_practice_pattern");
        sessionStorage.removeItem("greyrock_practice_pattern");
        const pattern = ALLOWED_PATTERNS.find(value => value === savedPattern) ?? "Emotional Pressure";
        navigate("/rehearsal", { replace: true, state: { entry_type: "free_preview", patterns: [{ name: pattern }] } });
      } else if (returnUrl && returnUrl !== "/" && returnUrl !== "/login") {
        navigate(returnUrl, { replace: true });
      } else if (pendingAnalysis) {
        localStorage.removeItem("greyrock_pending_analysis");
        navigate("/analysis-result", { replace: true, state: { message: pendingAnalysis } });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [user, loading, navigate, practiceReturn]);

  // If modal is closed without signing in, redirect to home
  const handleCloseModal = () => {
    setShowAuthModal(false);
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            background: "linear-gradient(180deg, hsl(0 0% 10%) 0%, hsl(0 0% 5%) 100%)",
          }}
        />
        <div className="relative z-10 flex flex-col items-center gap-4">
          <img
            src={logo}
            alt="GreyRock AI"
            className="w-16 h-16 object-contain animate-pulse"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <SEO
        title="Sign In — GreyRock AI®"
        description="Sign in to GreyRock AI to access your saved analyses, rehearsal sessions, response strategies and credit balance."
        path="/login"
        noindex
      />
      {/* Gradient background */}
      {/* Gradient background */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(180deg, hsl(0 0% 10%) 0%, hsl(0 0% 5%) 100%)",
        }}
      />

      {/* Subtle cyan glow at top */}
      <div
        className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] pointer-events-none opacity-15"
        style={{
          background: "radial-gradient(ellipse at center, hsl(184 100% 50% / 0.4) 0%, transparent 70%)",
        }}
      />

      <div className="relative z-10 container mx-auto px-4 py-12 flex flex-col items-center justify-center min-h-screen">
        <img
          src={logo}
          alt="GreyRock AI"
          className="w-24 h-24 object-contain drop-shadow-[0_0_25px_rgba(0,242,255,0.3)] mb-8"
        />
        <h1 className="text-2xl font-bold text-foreground mb-2">Welcome to GreyRock AI®</h1>
        <p className="text-muted-foreground mb-6">Sign in to continue</p>
      </div>

      <AuthModal 
        open={showAuthModal} 
        onOpenChange={(isOpen) => {
          if (!isOpen) handleCloseModal();
        }}
        defaultMode="signin"
      />
    </div>
  );
};

export default Login;
